import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader";
import { storage } from "./Firebase";
import * as THREE from "three";
import Toast from "./Toast";

const exrLoader = new EXRLoader();
const texLoader = new THREE.TextureLoader();
const TextureLibrary: { [key: string]: THREE.Texture } = {};

const loadAsBackgroundAndEnv = async (
  scene: THREE.Scene,
  name: string,
  storagePath: string
): Promise<void> => {
  if (name in TextureLibrary) {
    scene.background = TextureLibrary[name];
    scene.environment = TextureLibrary[name];
    Toast(`Cached[EnvMap]: ${name}`);
  } else {
    return await storage
      .ref(storagePath)
      .getDownloadURL()
      .then((value: string) => {
        exrLoader.load(
          // resource URL
          value,
          // called when the resource is loaded
          (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            scene.background = texture;
            scene.environment = texture;
            TextureLibrary[name] = texture;
            Toast(`Download[EnvMap]: ${name}`);
          },
          // called while loading is progressing
          (xhr) => {
            // console.log(`${name}: ` + (xhr.loaded / xhr.total) * 100 + "% loaded")
          },
          // called when loading has errors
          (error) => {
            // console.log(`An error happened when loading ${name}`)
          }
        );
      });
  }
};

const loadAsTexture = async (
  name: string,
  storagePath: string
): Promise<THREE.Texture> => {
  if (name in TextureLibrary) {
    return TextureLibrary[name];
  } else {
    return await storage
      .ref(storagePath)
      .getDownloadURL()
      .then((value: string) => {
        return texLoader.load(
          // resource URL
          value,
          // called when the resource is loaded
          (texture) => {
            TextureLibrary[name] = texture;
            Toast(`Download[TextureMap]: ${name}`);
          },
          // called while loading is progressing
          (xhr) => {
            // console.log(`${name}: ` + (xhr.loaded / xhr.total) * 100 + "% loaded")
          },
          // called when loading has errors
          (error) => {
            // console.log(`An error happened when loading ${name}`)
          }
        );
      });
  }
};

const GlassPassage = async (scene: THREE.Scene): Promise<void> => {
  return loadAsBackgroundAndEnv(
    scene,
    "GlassPassage",
    "/texture/glass_passage_4k.exr"
  );
};

const Snowfield = async (scene: THREE.Scene): Promise<void> => {
  return loadAsBackgroundAndEnv(
    scene,
    "Snowfield",
    "/texture/snow_field_4k.exr"
  );
};

const SataraNight = async (scene: THREE.Scene): Promise<void> => {
  return loadAsBackgroundAndEnv(
    scene,
    "SataraNight",
    "/texture/satara_night_2k.exr"
  );
};

interface TextureItem {
  colorMap: THREE.Texture | undefined;
  normalMap: THREE.Texture | undefined;
  heightMap: THREE.Texture | undefined;
  roughnessMap: THREE.Texture | undefined;
  occlusionMap: THREE.Texture | undefined;
}

const GreenFabric = async (): Promise<TextureItem> => {
  // const heightMap: THREE.Texture = await loadAsTexture(
  //   "GreenFabric",
  //   "/texture/green_fabric/2K-fabric_56_height.jpg"
  // );
  // const normalMap: THREE.Texture = await loadAsTexture(
  //   "GreenFabric",
  //   "/texture/green_fabric/2K-fabric_56_normal.jpg"
  // );
  // const colorMap: THREE.Texture = await loadAsTexture(
  //   "GreenFabric",
  //   "/texture/green_fabric/2K-fabric_56_baseColor.jpg"
  // );
  const roughnessMap: THREE.Texture = await loadAsTexture(
    "GreenFabric[Roughness]",
    "/texture/green_fabric/2K-fabric_56_roughness.jpg"
  );

  // return {
  //   colorMap: heightMap,
  //   normalMap: normalMap,
  //   heightMap: colorMap,
  //   roughnessMap: roughnessMap,
  // };

  return {
    colorMap: undefined,
    normalMap: undefined,
    heightMap: undefined,
    occlusionMap: undefined,
    roughnessMap: roughnessMap,
  };
};

const ConcreteMuddy = async (): Promise<TextureItem> => {
  const heightMap: THREE.Texture = await loadAsTexture(
    "ConcreteMuddy[Height]",
    "/texture/concrete_muddy/Concrete_Muddy_001_Height.png"
  );
  const normalMap: THREE.Texture = await loadAsTexture(
    "ConcreteMuddy[Normal]",
    "/texture/concrete_muddy/Concrete_Muddy_001_Normal.jpg"
  );
  const colorMap: THREE.Texture = await loadAsTexture(
    "ConcreteMuddy[Color]",
    "/texture/concrete_muddy/Concrete_Muddy_001_BaseColor.jpg"
  );
  const roughnessMap: THREE.Texture = await loadAsTexture(
    "ConcreteMuddy[Roughness]",
    "/texture/concrete_muddy/Concrete_Muddy_001_Roughness.jpg"
  );
  const occlusionMap: THREE.Texture = await loadAsTexture(
    "ConcreteMuddy[Occlusion]",
    "/texture/concrete_muddy/Concrete_Muddy_001_AmbientOcclusion.jpg"
  );

  return {
    colorMap: heightMap,
    normalMap: normalMap,
    heightMap: colorMap,
    occlusionMap: occlusionMap,
    roughnessMap: roughnessMap,
  };
};

const ConcreteMuddyPBR = async (texture: TextureItem): Promise<THREE.MeshStandardMaterial> => {
  const material = new THREE.MeshStandardMaterial({
    // Color
    map: texture.colorMap as THREE.Texture,
    // Normal
    normalMap: texture.normalMap,
    normalScale: new THREE.Vector2(1, 1),
    // Displacement
    displacementMap: texture.heightMap,
    displacementScale: 0.1,
    displacementBias: -0.05,
    // Roughness
    roughnessMap: texture.roughnessMap,
    roughness: 0.8,
    // Ambient Occlusion
    aoMap: texture.occlusionMap, 
    aoMapIntensity: 0.1,
    emissiveIntensity: 10
  });
  return material
}

export { GreenFabric, GlassPassage, Snowfield, SataraNight, ConcreteMuddy };
